/**
 * GuestCheckoutForm — Phase 10
 *
 * Inline customer-info + OTP step shown when the checkout has no authenticated
 * customer session. On successful OTP verify, calls the backend in one shot:
 *   POST /api/v2/checkout/verify-otp-and-init
 *      → creates/updates the customer
 *      → creates a pending booking with reserved inventory
 *      → returns { access_token, customer, booking }
 *
 * The parent (`CheckoutPage`) receives `{ token, customer, booking }` and
 * advances the flow to the payment step without any redirect.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Phone, User, ShieldCheck, RotateCcw, Check } from 'lucide-react';
import { toast } from 'sonner';
import { publicApi, customerV2Api } from '../../lib/api';

const OTP_RESEND_COOLDOWN_SEC = 30;
const OTP_LENGTH = 6;

function normalizePhoneInput(raw) {
  if (!raw) return '';
  let v = raw.replace(/[^\d+]/g, '');
  // Pre-pend +91 if user typed 10 digits without prefix
  if (/^\d{10}$/.test(v)) v = '+91' + v;
  return v;
}

export default function GuestCheckoutForm({
  bookingDraft,            // { event_id, event_title, items: [{pass_type_id, quantity}], total, ... }
  walletAmount = 0,
  onSuccess,               // ({ token, customer, booking }) => void
  prefillName = '',
  prefillPhone = '',
}) {
  const [step, setStep] = useState('info'); // info | otp
  const [name, setName] = useState(prefillName);
  const [phone, setPhone] = useState(prefillPhone);
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const phoneRef = useRef(null);

  // Resend cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const validInfo = () => {
    if (name.trim().length < 2) return 'Please enter your full name.';
    const p = normalizePhoneInput(phone);
    if (!/^\+\d{10,15}$/.test(p)) return 'Please enter a valid mobile number (with country code).';
    return null;
  };

  // ── Send OTP ──────────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    const err = validInfo();
    if (err) {
      setError(err);
      toast.error(err);
      return;
    }
    setError(null);
    setSending(true);
    try {
      const normalized = normalizePhoneInput(phone);
      setPhone(normalized);
      const res = await publicApi.sendOTPSmart(normalized);
      const channel = res?.data?.channel || res?.data?.delivery || 'sms';
      toast.success(`OTP sent via ${String(channel).toUpperCase()} to ${normalized.slice(0, 4)}****${normalized.slice(-3)}`);
      setStep('otp');
      setCooldown(OTP_RESEND_COOLDOWN_SEC);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not send OTP. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Could not send OTP.');
      toast.error(typeof msg === 'string' ? msg : 'Could not send OTP.');
    } finally {
      setSending(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    if (cooldown > 0) return;
    setOtp('');
    setError(null);
    setSending(true);
    try {
      await publicApi.sendOTPSmart(phone);
      toast.success('OTP resent');
      setCooldown(OTP_RESEND_COOLDOWN_SEC);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not resend OTP.';
      toast.error(typeof msg === 'string' ? msg : 'Could not resend OTP.');
    } finally {
      setSending(false);
    }
  };

  // ── Verify OTP + initialize checkout (single round-trip) ──────────────────
  const handleVerifyAndInit = async () => {
    if (otp.length !== OTP_LENGTH) {
      setError(`Enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }
    setError(null);
    setVerifying(true);
    try {
      const payload = {
        full_name: name.trim(),
        phone,
        otp:       otp.trim(),
        event_id:  bookingDraft.event_id,
        items:     bookingDraft.items.map((i) => ({
          pass_type_id: i.pass_type_id,
          quantity:     i.quantity,
        })),
        wallet_amount: walletAmount,
      };
      const res = await customerV2Api.guestCheckoutInit(payload);
      const { access_token, customer, booking, user_created } = res.data || {};
      if (!access_token || !customer || !booking) {
        throw new Error('Unexpected response from server.');
      }
      toast.success(user_created ? 'Welcome! Your account was created.' : 'Welcome back!');
      onSuccess({ token: access_token, customer, booking });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : (detail?.message || e?.message || 'Verification failed.');
      setError(msg);
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  // ── UI ───────────────────────────────────────────────────────────────────
  return (
    <div
      className="surface-card rounded-xl p-5 lg:p-6 space-y-4"
      data-testid="guest-checkout-form"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={18} className="text-[#D4A843]" />
        </div>
        <div>
          <h3 className="font-['EB_Garamond'] text-xl text-[#F2F2F2] leading-tight">
            Your Details
          </h3>
          <p className="text-xs text-[#B9B9B9] mt-0.5">
            We'll send a one-time code to verify your number.
            {' '}No password needed.
          </p>
        </div>
      </div>

      {step === 'info' && (
        <div className="space-y-3" data-testid="guest-step-info">
          {/* Name */}
          <label className="block">
            <span className="block text-xs text-[#B9B9B9] mb-1 uppercase tracking-wider">Full Name</span>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="As on government ID"
                className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2.5 text-[#F2F2F2] focus:border-[#D4A843] focus:outline-none transition-colors"
                maxLength={80}
                data-testid="guest-name-input"
              />
            </div>
          </label>

          {/* Phone */}
          <label className="block">
            <span className="block text-xs text-[#B9B9B9] mb-1 uppercase tracking-wider">Mobile Number</span>
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
              <input
                ref={phoneRef}
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91XXXXXXXXXX"
                className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2.5 text-[#F2F2F2] focus:border-[#D4A843] focus:outline-none transition-colors"
                data-testid="guest-phone-input"
              />
            </div>
            <span className="text-[10px] text-[#7A7A7A] mt-1 block">
              We'll send your ticket here via WhatsApp.
            </span>
          </label>

          {error && (
            <p className="text-xs text-red-400" data-testid="guest-info-error">{error}</p>
          )}

          <button
            type="button"
            onClick={handleSendOtp}
            disabled={sending}
            className="w-full bg-[#D4A843] text-[#0A0A0A] py-3 rounded-lg font-semibold tracking-wide hover:bg-[#C49A35] active:bg-[#B58A25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="guest-send-otp-btn"
          >
            {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : 'Send OTP'}
          </button>
        </div>
      )}

      {step === 'otp' && (
        <div className="space-y-3" data-testid="guest-step-otp">
          <div className="bg-[#0F0F0F] border border-[#202020] rounded-lg p-3 text-xs text-[#B9B9B9] flex items-center justify-between">
            <span>OTP sent to <span className="text-[#F2F2F2] tabular-nums">{phone}</span></span>
            <button
              type="button"
              onClick={() => { setStep('info'); setOtp(''); setError(null); }}
              className="text-[#D4A843] hover:text-[#C49A35] transition-colors"
              data-testid="guest-change-phone-btn"
            >
              Change
            </button>
          </div>

          <label className="block">
            <span className="block text-xs text-[#B9B9B9] mb-1 uppercase tracking-wider">Enter 6-digit OTP</span>
            <input
              type="tel"
              inputMode="numeric"
              autoFocus
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
              placeholder="••••••"
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg px-3 py-3 text-[#F2F2F2] text-2xl tracking-[0.4em] text-center tabular-nums focus:border-[#D4A843] focus:outline-none transition-colors"
              maxLength={OTP_LENGTH}
              data-testid="guest-otp-input"
            />
          </label>

          {error && (
            <p className="text-xs text-red-400" data-testid="guest-otp-error">{error}</p>
          )}

          <button
            type="button"
            onClick={handleVerifyAndInit}
            disabled={verifying || otp.length !== OTP_LENGTH}
            className="w-full bg-[#D4A843] text-[#0A0A0A] py-3 rounded-lg font-semibold tracking-wide hover:bg-[#C49A35] active:bg-[#B58A25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="guest-verify-btn"
          >
            {verifying
              ? <><Loader2 size={16} className="animate-spin" /> Verifying…</>
              : <><Check size={16} /> Verify & Continue</>}
          </button>

          <button
            type="button"
            onClick={handleResendOtp}
            disabled={cooldown > 0 || sending}
            className="w-full bg-transparent border border-[#2A2A2A] text-[#B9B9B9] py-2.5 rounded-lg text-sm hover:border-[#D4A843] hover:text-[#D4A843] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="guest-resend-otp-btn"
          >
            <RotateCcw size={14} />
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
          </button>
        </div>
      )}
    </div>
  );
}
